import React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { IconCalendarEvent, IconPlus } from '@tabler/icons-react';
import {
  Button, EmptyState, ErrorState, Segmented, Skeleton,
} from '../../components/common';
import AppointmentCard from '../../components/booking/AppointmentCard';
import CancelSheet from '../../components/booking/CancelSheet';
import appointmentApi from '../../api/appointment/appointmentApi';
import useFetch from '../../hooks/useFetch';
import useCancelAppointment from './useCancelAppointment';
import { PATHS } from '../../routes/paths';

const TABS = [
  { key: 'upcoming', label: 'Upcoming' },
  { key: 'past', label: 'Past' },
  { key: 'cancelled', label: 'Cancelled' },
];

const EMPTY = {
  upcoming: { title: 'No upcoming appointments', body: 'Book a visit and it will show up here.' },
  past: { title: 'No past visits yet', body: 'Completed visits and their summaries appear here.' },
  cancelled: { title: 'No cancelled appointments', body: 'Appointments you cancel will appear here.' },
};

export default function AppointmentsPage() {
  const [params, setParams] = useSearchParams();
  const tab = TABS.some((t) => t.key === params.get('tab')) ? params.get('tab') : 'upcoming';
  const summary = useFetch(() => appointmentApi.summary(), [], { initial: null });
  const list = useFetch(() => appointmentApi.list(tab), [tab], { initial: [] });
  const cancel = useCancelAppointment(() => {
    list.reload();
    summary.reload();
  });

  const items = TABS.map((t) => ({
    key: t.key,
    label: summary.data ? `${t.label} · ${summary.data[t.key]}` : t.label,
  }));

  let content;
  if (list.loading && !list.data.length) content = <Skeleton count={3} height={180} />;
  else if (list.error) content = <ErrorState error={list.error} onRetry={list.reload} />;
  else if (!list.data.length) {
    content = (
      <EmptyState
        icon={<IconCalendarEvent size={26} />}
        title={EMPTY[tab].title}
        action={tab === 'upcoming' && <Button to={PATHS.doctors}>Book an appointment</Button>}
      >
        {EMPTY[tab].body}
      </EmptyState>
    );
  } else {
    content = (
      <div className="u-stack">
        {list.data.map((a) => <AppointmentCard key={a.id} appointment={a} tab={tab} onCancel={cancel.open} />)}
      </div>
    );
  }

  return (
    <div className="page page--with-nav appointments">
      <div className="appointments__top">
        <div className="u-flex u-items-center u-justify-between">
          <h1 className="page__title">Appointments</h1>
          <Link className="icon-btn icon-btn--primary" to={PATHS.doctors} aria-label="Book an appointment">
            <IconPlus size={26} />
          </Link>
        </div>
        <Segmented
          className="u-mt-4"
          items={items}
          value={tab}
          onChange={(key) => setParams(key === 'upcoming' ? {} : { tab: key }, { replace: true })}
          ariaLabel="Appointment filter"
        />
      </div>
      <div className="page__body">{content}</div>
      <CancelSheet appointment={cancel.target} onClose={cancel.close} onConfirm={cancel.confirm} loading={cancel.loading} />
    </div>
  );
}
